import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  People,
  ExpandMore,
  ExpandLess,
  ArrowForward,
} from '@mui/icons-material';
import { format } from 'date-fns';

const EventListItem = ({ event, isRegistered }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  // Convert Firestore Timestamp to Date
  const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);

  // Format the date
  const formattedDate = format(eventDate, 'EEEE, MMMM d, yyyy');
  const dayOfWeek = format(eventDate, 'EEE');
  const monthDay = format(eventDate, 'MMM d');

  // Determine status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'primary';
      case 'in-progress':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        overflow: 'hidden',
        transition: 'all 0.3s',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <ListItem
        button
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 2,
        }}
      >
        {/* Date Box */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: { xs: 60, sm: 70 },
            p: { xs: 0.75, sm: 1 },
            bgcolor: isRegistered ? 'success.main' : 'primary.main',
            color: 'white',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
            {dayOfWeek.toUpperCase()}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
            {format(eventDate, 'd')}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {format(eventDate, 'MMM')}
          </Typography>
        </Box>

        {/* Event Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {event.title}
            </Typography>
            {isRegistered && (
              <Chip
                label="✓ Scheduled"
                size="small"
                color="success"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            <Chip
              label={event.status}
              size="small"
              color={getStatusColor(event.status)}
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 0.75, sm: 2 },
              mb: 1,
            }}
          >
            {event.startTime && event.endTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {event.startTime} - {event.endTime}
                </Typography>
              </Box>
            )}

            {event.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {event.location}
                </Typography>
              </Box>
            )}

            {event.volunteersNeeded > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <People fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {event.volunteersRegistered || 0}/{event.volunteersNeeded} volunteers
                </Typography>
              </Box>
            )}
          </Box>

          {!expanded && event.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {event.description}
            </Typography>
          )}
        </Box>

        {/* Expand Icon */}
        <IconButton size="small" sx={{ minWidth: 48, minHeight: 48 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ListItem>

      {/* Expanded Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {event.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {event.description}
              </Typography>
            </Box>
          )}

          {event.skillsRequired && event.skillsRequired.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Skills Required
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {event.skillsRequired.map((skill, index) => (
                  <Chip key={index} label={skill} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Full date: {formattedDate}
          </Typography>

          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/events/${event.eventId}`);
            }}
            fullWidth
            sx={{ minHeight: 48 }}
          >
            View Details & Sign Up
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

const EventsList = ({ events, userRegistrations = [], emptyMessage = 'No events found' }) => {
  // Helper function to check if user is registered for an event
  const isUserRegistered = (eventId) => {
    return userRegistrations.some(reg => reg.eventId === eventId);
  };

  if (!events || events.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CalendarToday sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <List sx={{ width: '100%', p: 0 }}>
      {events.map((event) => (
        <EventListItem
          key={event.eventId}
          event={event}
          isRegistered={isUserRegistered(event.eventId)}
        />
      ))}
    </List>
  );
};

export default EventsList;
